import {
  UsePrivileges,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { Box, Stack, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { restartSyncTransactions } from "../../../../services/syncTransaction.service";

const translationPath = "page.4uBus.logs.syncTransaction.";

const RESTART_DOMAIN_NAME = "4ubuslogssynctransactionrestart";

export const SYNC_TRANSACTION_ERROR_STATUS = "ERRO";

export const isSyncTransactionError = (status?: string | null) =>
  status?.toUpperCase() === SYNC_TRANSACTION_ERROR_STATUS;

interface IRestartOutcome {
  executed: boolean;
  restartedCount: number;
}

const NOT_EXECUTED: IRestartOutcome = { executed: false, restartedCount: 0 };

const useRestartSyncTransactions = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { setMessage } = useNotificationContext();
  const { privileges } = UsePrivileges();
  const { sendRequest, loading } = useFetch(restartSyncTransactions);

  const canRestart = useMemo(
    () =>
      privileges.some(
        (privilege) =>
          privilege.domainName === "all" ||
          privilege.domainName === RESTART_DOMAIN_NAME,
      ),
    [privileges],
  );

  const restart = useCallback(
    async (identifiers: number[]): Promise<IRestartOutcome> => {
      const uniqueIdentifiers = Array.from(
        new Set(identifiers.filter((identifier) => !Number.isNaN(identifier))),
      );

      if (!uniqueIdentifiers.length) {
        setMessage({
          message: t(translationPath + "resend_select_rows"),
          type: "error",
        });
        return NOT_EXECUTED;
      }

      try {
        await confirm({
          title: t(translationPath + "resend_confirm_title"),
          description: t(translationPath + "resend_confirm_description", {
            quantity: uniqueIdentifiers.length,
          }),
          confirmationText: t(translationPath + "resend_confirm_button"),
          cancellationText: t(translationPath + "resend_cancel"),
        });
      } catch {
        return NOT_EXECUTED;
      }

      const { success, data } = await sendRequest({
        identifiers: uniqueIdentifiers,
      });

      if (!success || !Array.isArray(data)) return NOT_EXECUTED;

      const restarted = data.filter((item) => item.restarted);
      const ignored = data.filter(
        (item) =>
          !item.restarted &&
          item.newStatus &&
          !isSyncTransactionError(item.newStatus),
      );
      const failed = data.filter(
        (item) => !item.restarted && !ignored.includes(item),
      );

      await confirm({
        title: t(translationPath + "resend_result_title"),
        description: (
          <Stack gap={1.5}>
            <Typography>
              {t(translationPath + "resend_result_summary", {
                restarted: restarted.length,
                ignored: ignored.length,
                failed: failed.length,
              })}
            </Typography>
            {failed.length > 0 && (
              <Box>
                <Typography fontWeight={600} mb={0.5}>
                  {t(translationPath + "resend_result_failed_title")}
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                  {failed.map((item) => (
                    <Typography key={item.identifier} variant="body2">
                      #{item.identifier}: {item.message || "-"}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              {t(translationPath + "resend_result_source_hint")}
            </Typography>
          </Stack>
        ),
        confirmationText: t(translationPath + "resend_result_close"),
        hideCancelButton: true,
      }).catch(() => undefined);

      return { executed: true, restartedCount: restarted.length };
    },
    [confirm, sendRequest, setMessage, t],
  );

  return { canRestart, restart, loading };
};

export default useRestartSyncTransactions;
